import React, { useState, useEffect } from "react";
import { Copy, Check } from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark, oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Link } from "react-router-dom";
import { PublicLayout } from "../components/PublicLayout";

function useIsDark() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setDark(document.documentElement.classList.contains("dark"));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

function CodeBlock({ title, language, langKey, children, isDark }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl border border-[var(--bd)] overflow-hidden bg-[var(--surface)]">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--surface2)] border-b border-[var(--bd)]">
        <span className="font-heading font-bold text-sm">{title}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-[var(--t2)] uppercase">{language}</span>
          <button onClick={copy} className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-[var(--accent-soft)] transition-colors text-[var(--t2)] hover:text-[var(--accent)]">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      </div>
      <SyntaxHighlighter
        language={langKey || "cpp"}
        style={isDark ? oneDark : oneLight}
        customStyle={{ margin: 0, borderRadius: 0, fontSize: "0.8rem", lineHeight: 1.6 }}
        showLineNumbers
        lineNumberStyle={{ color: isDark ? "#4a5568" : "#9ca3af", fontSize: "0.7rem", minWidth: "2.5em" }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

const SNIPPETS = [
  {
    title: "WiFi Failover + HiveMQ MQTT (TLS)",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <WiFi.h>
#include <WiFiClientSecure.h>
#include <PubSubClient.h>

// Multiple networks — board tries each in order
struct WifiCred { const char* ssid; const char* pass; };
static const WifiCred WIFI_NETS[] = {
  { "hotspot1",            "password1" },
  { "HUAWEI_B311_548F",    "routerpass" },
};

const char* MQTT_HOST = "xxxx.s1.eu.hivemq.cloud";
const int   MQTT_PORT = 8883;          // TLS port
const char* MQTT_USER = "cput_energy";
const char* MQTT_PASS = "yourpass";
const char* PZEM_TOPIC     = "campus/energy/pzem";
const char* PZEM_CMD_TOPIC = "campus/energy/pzem_cmd";

WiFiClientSecure tlsClient;
PubSubClient     mqtt(tlsClient);

void connectWifi() {
  WiFi.mode(WIFI_STA);
  for (auto& net : WIFI_NETS) {
    WiFi.begin(net.ssid, net.pass);
    unsigned long t = millis();
    while (WiFi.status() != WL_CONNECTED && millis() - t < 15000)
      delay(500);
    if (WiFi.status() == WL_CONNECTED) return;
    WiFi.disconnect(true);
  }
}

bool connectMQTT() {
  tlsClient.setInsecure();              // accept HiveMQ TLS without CA bundle
  mqtt.setServer(MQTT_HOST, MQTT_PORT);
  mqtt.setKeepAlive(60);
  mqtt.setBufferSize(1024);             // PZEM JSON is ~520 bytes — default 256 too small!
  String id = "pzem-" + String((uint32_t)ESP.getEfuseMac(), HEX);
  if (mqtt.connect(id.c_str(), MQTT_USER, MQTT_PASS)) {
    mqtt.subscribe(PZEM_CMD_TOPIC);
    return true;
  }
  return false;
}`,
  },
  {
    title: "PZEM-004T 3-Phase Reading (Modbus RTU)",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <PZEM004Tv30.h>

// All 3 PZEMs share Serial2
// GPIO 26 = RX (ESP32 ← all PZEM TX)
// GPIO 27 = TX (ESP32 → all PZEM RX)
// PZEM addresses must be pre-set: 0x01 / 0x02 / 0x03
PZEM004Tv30 pzemL1(&Serial2, 26, 27, 0x01);  // L1
PZEM004Tv30 pzemL2(&Serial2, 26, 27, 0x02);  // L2
PZEM004Tv30 pzemL3(&Serial2, 26, 27, 0x03);  // L3

void readAllPhases() {
  float v1 = pzemL1.voltage(), a1 = pzemL1.current(),
        w1 = pzemL1.power(),   e1 = pzemL1.energy(),
        h1 = pzemL1.frequency(), p1 = pzemL1.pf();
  delay(300);  // 300ms gap between modules
  float v2 = pzemL2.voltage(), a2 = pzemL2.current(),
        w2 = pzemL2.power(),   e2 = pzemL2.energy();
  delay(300);
  float v3 = pzemL3.voltage(), a3 = pzemL3.current(),
        w3 = pzemL3.power(),   e3 = pzemL3.energy();

  if (!isnan(v1))
    Serial.printf("L1: %.1fV  %.3fA  %.1fW  %.1fHz  PF:%.2f\\n",
                  v1, a1, w1, h1, p1);
  // repeat for L2, L3
}`,
  },
  {
    title: "DHT11 + Fan Auto (LEDC PWM)",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <DHT.h>

#define DHT_PIN     17   // DHT11 data (GPIO 17)
#define DHT_TYPE    DHT11
#define FAN_PIN     25   // 12V fan via IRLZ44N (GPIO 25)
#define FAN_CHANNEL  2   // LEDC channel 2
#define FAN_FREQ    25   // 25 Hz — low freq for 3-pin fan torque
#define FAN_BITS     8   // 8-bit duty (0-255)

DHT dht(DHT_PIN, DHT_TYPE);

void setup() {
  dht.begin();
  ledcSetup(FAN_CHANNEL, FAN_FREQ, FAN_BITS);
  ledcAttachPin(FAN_PIN, FAN_CHANNEL);
}

void handleFan(float temp, float hum) {
  int duty = 0;
  if (temp > 35.0 || hum > 85.0) duty = 255;       // 100%
  else if (temp > 32.0 || hum > 70.0) duty = 153;  // 60%
  else if (temp > 30.0) duty = 76;                   // 30%
  ledcWrite(FAN_CHANNEL, duty);
}

void loop() {
  float h = dht.readHumidity();
  float t = dht.readTemperature();
  if (!isnan(h) && !isnan(t)) {
    handleFan(t, h);
    Serial.printf("[DHT11] %.1fC  %.0f%%RH\\n", t, h);
  }
  delay(2000);
}`,
  },
  {
    title: "MQ-135 Gas Sensor (Voltage Divider)",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `// GPIO 34 — ADC1_CH6 (input-only, safe with WiFi active)
// Voltage divider: AOUT → 10kΩ → GPIO34 → 22kΩ → GND
// 5V × 22/(10+22) = 3.44V → safe for ESP32 ADC
#define MQ135_PIN  34

float gasWarnPpm = 600.0f;  // configurable via MQTT command
float gasPpm     = 0.0f;
char  gasLabel[8] = "GOOD";

void readGasSensor() {
  long sum = 0;
  for (int i = 0; i < 5; i++) { sum += analogRead(MQ135_PIN); delay(5); }
  int raw = (int)(sum / 5);
  gasPpm = (raw / 4095.0f) * 1000.0f;

  if      (raw < 1500) strcpy(gasLabel, "GOOD");
  else if (raw < 2500) strcpy(gasLabel, "POOR");
  else                 strcpy(gasLabel, "DANGER");

  Serial.printf("[GAS] Raw:%d  PPM:%.0f  Quality:%s\\n", raw, gasPpm, gasLabel);
}`,
  },
  {
    title: "PIR Light Hub + Manual Override",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `// GPIO 4  — HC-SR501 PIR output
// GPIO 32 — Light hub via IRLZ44N (100Ω gate resistor)
// GPIO 18 — Piezo buzzer via TIP120 (1kΩ base resistor)
#define PIR_PIN        4
#define LIGHT_PIN     32
#define LIGHT_CHANNEL  3
#define LIGHT_TIMEOUT  5000UL  // ms before auto-off on no motion

bool          lightManual  = false;  // true = user set via MQTT; PIR won't auto-dim
int           lightDuty    = 0;
unsigned long lastMotionAt = 0;

void setLight(int duty) {
  lightDuty = constrain(duty, 0, 255);
  ledcWrite(LIGHT_CHANNEL, lightDuty);
}

void handlePIRLight() {
  if (lightManual) return;   // manual override active — skip PIR logic
  bool motion = (digitalRead(PIR_PIN) == HIGH);
  if (motion) {
    lastMotionAt = millis();
    if (lightDuty == 0) setLight(255);  // fade in on motion
  } else if (lightDuty > 0 && millis() - lastMotionAt >= LIGHT_TIMEOUT) {
    setLight(0);  // fade out after 5s of no motion
  }
}

// MQTT command: {"cmd":"light","val":"75"}  → sets 75%, locks manual mode
// MQTT command: {"cmd":"light","val":"off"} → turns off, releases manual mode`,
  },
  {
    title: "MQTT JSON Publish (~520 bytes)",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <ArduinoJson.h>

// Topic: campus/energy/pzem  (retained=true, QoS 0)
// NOTE: mqtt.setBufferSize(1024) — default 256 is too small for ~520-byte payload!
void publishPZEM() {
  StaticJsonDocument<900> doc;

  // Phase readings — lowercase keys: l1, l2, l3
  auto addPhase = [&](const char* key, PZEM004Tv30& pzem) {
    float v = pzem.voltage();
    bool ok = !isnan(v);
    JsonObject o = doc[key].to<JsonObject>();
    o["v"]  = ok ? roundf(v   * 10) / 10.0f : 0.0f;
    o["a"]  = ok ? roundf(pzem.current() * 1000) / 1000.0f : 0.0f;
    o["w"]  = ok ? roundf(pzem.power()   * 10) / 10.0f : 0.0f;
    o["e"]  = ok ? pzem.energy() : 0.0f;
    o["hz"] = ok ? roundf(pzem.frequency() * 10) / 10.0f : 0.0f;
    o["pf"] = ok ? roundf(pzem.pf() * 100) / 100.0f : 0.0f;
    o["ok"] = ok;
  };
  addPhase("l1", pzemL1);
  addPhase("l2", pzemL2);
  addPhase("l3", pzemL3);

  doc["dht_temp"]  = dhtTemp;
  doc["dht_hum"]   = dhtHumidity;
  doc["dht_ok"]    = dhtOk;
  doc["gas_ppm"]   = gasPpm;
  doc["gas_quality"] = gasLabel;
  doc["fan_pct"]   = fanPercent;
  doc["fan_auto"]  = fanAuto;
  doc["light_pct"] = (int)round(lightDuty / 255.0f * 100);
  doc["pir"]       = (bool)(digitalRead(PIR_PIN) == HIGH);
  doc["ip"]        = WiFi.localIP().toString();
  doc["uptime"]    = millis() / 1000;

  char buf[950];
  size_t n = serializeJson(doc, buf);
  mqtt.publish("campus/energy/pzem", (uint8_t*)buf, n, true);
}`,
  },
  {
    title: "MQTT Command Handler",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `// Topic: campus/energy/pzem_cmd
// Payload: {"cmd":"<name>","val":"<value>"}
// Supported commands: fan | light | lcd | pir | buzzer | gas_thresh | fan_gas

void applyCmd(const String& cmd, const String& val) {
  if (cmd == "fan") {
    if (val == "auto")    { fanAuto = true; }
    else if (val == "off"){ fanAuto = false; setFan(0); }
    else                  { fanAuto = false; setFan(val.toInt() * 255 / 100); }

  } else if (cmd == "light") {
    if (val == "off")     { lightManual = false; setLight(0); }
    else if (val == "on") { lightManual = true;  setLight(255); }
    else                  { lightManual = true;  setLight(val.toInt() * 255 / 100); }

  } else if (cmd == "pir") {
    pirEnabled = (val == "on");
    if (!pirEnabled) setLight(0);

  } else if (cmd == "buzzer") {
    if (val == "beep")  beepOk();
    if (val == "alert") beepError();
    if (val == "stop")  noTone(BUZZER_PIN);

  } else if (cmd == "gas_thresh") {
    gasWarnPpm = val.toFloat();
  }
}

void mqttCallback(char* topic, byte* payload, unsigned int len) {
  char buf[64]; memcpy(buf, payload, len); buf[len] = '\\0';
  StaticJsonDocument<128> doc;
  if (deserializeJson(doc, buf) == DeserializationError::Ok)
    applyCmd(doc["cmd"] | "", doc["val"] | "");
}`,
  },
];

export default function CodePublic() {
  const isDark = useIsDark();
  const [filter, setFilter] = useState("All");
  const tags = ["All", "Sensors", "MQTT", "Control", "Display"];

  const tagMap = {
    Sensors: [0, 1, 2, 3],
    MQTT: [0, 6, 7],
    Control: [4, 7],
    Display: [5],
  };

  const visible = filter === "All" ? SNIPPETS : SNIPPETS.filter((_, i) => tagMap[filter]?.includes(i));

  return (
    <PublicLayout>
      <section className="w-full px-4 sm:px-8 lg:px-12 py-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">Source Code</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">Code Snippets</h1>
            <p className="text-[var(--t2)] mt-3">Core Arduino/ESP32 firmware snippets from the Campus Energy Monitor. Copy and adapt for your own IoT projects.</p>
            <p className="text-xs text-[var(--t2)] font-mono mt-2">ESP32 Arduino Core v2.0.17, PlatformIO / Arduino IDE</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {tags.map((t) => (
              <button key={t} onClick={() => setFilter(t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === t ? "bg-[var(--accent)] text-black border-[var(--accent)]" : "bg-[var(--surface2)] border-[var(--bd)] text-[var(--t2)] hover:border-[var(--bd-strong)]"}`}>
                {t}
              </button>
            ))}
          </div>

          <div className="space-y-6">
            {visible.map((s, i) => (
              <CodeBlock key={i} title={s.title} language={s.language} langKey={s.langKey} isDark={isDark}>{s.code}</CodeBlock>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-[var(--bd)] bg-[var(--surface)] px-6 py-8 text-center">
            <p className="font-heading font-bold text-lg">Need the full source or guidance?</p>
            <p className="text-sm text-[var(--t2)] mt-2 max-w-md mx-auto leading-relaxed">These snippets cover the key patterns. If you need the complete firmware file <span className="font-mono text-[var(--accent)]">pzem_boards.ino</span> or want to discuss the project in detail, reach out directly.</p>
            <Link to="/contact" className="inline-flex mt-5 px-6 py-2.5 rounded-md bg-[var(--accent)] text-black text-sm font-semibold active:scale-95 transition-transform">Contact Henock Mukonkole</Link>
            <p className="text-xs text-[var(--t2)] font-mono mt-5">Built by Henock Mukonkole (HNK), CPUT 2026</p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
